'use client';

import { useMemo, useState } from 'react';
import CurrencySelector from '@/components/utils/CurrencySelector';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import { calculateDividendYield } from './calculations';
import { DividendYieldValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  translations: ToolsTranslations['dividendYield'];
}

export default function DividendYieldCalculator({ translations }: Props) {
  const [values, setValues] = useState<DividendYieldValues>({
    pricePerShare: 250,
    dividendPerShare: 12,
    currency: 'kr',
  });

  const handleValuesChange = (field: string | number | symbol, value: number | string) => {
    const key = field as keyof DividendYieldValues;
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const result = useMemo(() => calculateDividendYield(values), [values]);

  return (
    <section id="dividend-yield-calculator" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
              <p className="text-sm text-gray-600 mt-0.5">{translations.subtitle}</p>
            </div>
            <div className="w-56">
              <CurrencySelector
                value={values.currency}
                onChange={(currency) => handleValuesChange('currency', currency)}
                showArrow={false}
                localStorageKey="currencyPreference"
              />
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CalculatorInputs values={values} onValuesChange={handleValuesChange} translations={translations} />
            <ResultsDisplay result={result} translations={translations} />
          </div>
        </div>
      </div>
    </section>
  );
}


